import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './FeaturedEventsAdmin.css';

const FeaturedEventsAdmin = () => {
    const [events, setEvents] = useState([]);
    const [featuredEvents, setFeaturedEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState({});
    const navigate = useNavigate();
    const [localChanges, setLocalChanges] = useState({});
    // 💰 Fee de plataforma por evento (solo Superadministrador)
    const [feeConfigs, setFeeConfigs] = useState({}); // { [slug]: { is_enabled, fee_type, fee_value, expanded, loaded, saving } }

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const [allRes, featuredRes] = await Promise.all([
                    api.get('/admin/events/events'),
                    api.get('/admin/events/featured-events')
                ]);
                
                setEvents(allRes.data);
                setFeaturedEvents(featuredRes.data);
                
                const initialChanges = {};
                featuredRes.data.forEach(event => {
                    initialChanges[event.slug] = {
                        end_date: event.end_date,
                        event_status: event.event_status || 'scheduled',
                        status_message: event.status_message || '',
                        location_name: event.location_name || '',
                        location_address: event.location_address || '',
                        location_city: event.location_city || '',
                        location_country: event.location_country || 'Bolivia',
                        location_map_url: event.location_map_url || '',
                        schedule_data: event.schedule_data || null, 
                        hasChanges: false
                    };
                });
                setLocalChanges(initialChanges);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    const sanitizeText = (text) => {
        if (!text) return '';

        return text.replace(/<[^>]*>/g, '').trim();
    };
    
    const updateLocalChange = (slug, field, value) => {
    
        const sanitizedValue = (typeof value === 'string') ? sanitizeText(value) : value;
        
        setLocalChanges(prev => ({
            ...prev,
            [slug]: {
                ...prev[slug],
                [field]: sanitizedValue,
                hasChanges: true
            }
        }));
    };

    const isValidUrl = (string) => {
        try {
            const url = new URL(string);
            return url.protocol === 'http:' || url.protocol === 'https:';
        } catch (_) {
            return false;
        }
    };
    

    const saveEventChanges = async (slug) => {
        const changes = localChanges[slug];
        if (!changes || !changes.hasChanges) {
           
            return;
        }
        
        setSaving(prev => ({ ...prev, [slug]: true }));
        try {
            if (changes.event_status === 'scheduled' && !changes.end_date) {
                alert('La fecha del evento es requerida para eventos confirmados');
                setSaving(prev => ({ ...prev, [slug]: false }));
                return;
            }
   
            const promises = [];
   
            // 1. Fecha
            if (changes.end_date !== undefined) {
                let endDate = changes.end_date;
                if (endDate) {
                    const dateObj = new Date(endDate);
                    dateObj.setHours(3, 0, 0, 0);
                    endDate = dateObj;
                }
                promises.push(api.patch(`/admin/events/featured-events/${slug}/date`, {
                    end_date: endDate ? endDate.toISOString() : null
                }));
            }
   
            // 2. Estado
            if (changes.event_status !== undefined || changes.status_message !== undefined) {
                promises.push(api.patch(`/admin/events/featured-events/${slug}/status`, {
                    event_status: changes.event_status,
                    status_message: changes.status_message
                }));
            }
   
            // 3. Ubicación
            if (changes.location_map_url && !isValidUrl(changes.location_map_url)) {
                alert('La URL de Google Maps no es válida');
                setSaving(prev => ({ ...prev, [slug]: false }));
                return;
            }
            if (changes.location_name !== undefined || changes.location_address !== undefined ||
                changes.location_city !== undefined || changes.location_country !== undefined ||
                changes.location_map_url !== undefined) {
                promises.push(api.patch(`/admin/events/featured-events/${slug}/location`, {
                    location_name: changes.location_name,
                    location_address: changes.location_address,
                    location_city: changes.location_city,
                    location_country: changes.location_country,
                    location_map_url: changes.location_map_url
                }));
            }
   
            // 🆕 4. CRONOGRAMA (Schedule Data) - CORREGIDO Y CON DEBUG
            if (changes.schedule_data !== undefined) {
                let parsedSchedule = changes.schedule_data;
                
   
                if (typeof parsedSchedule === 'string') {
                    if (parsedSchedule.trim() === '') {
                        parsedSchedule = null; // Si está vacío, lo tratamos como null para borrar en BD
                        
                    } else {
                        try {
                            parsedSchedule = JSON.parse(parsedSchedule);
                            
                        } catch (e) {
                            console.error('❌ [DEBUG] Error al parsear JSON:', e);
                            alert('⚠️ El formato del JSON del cronograma es inválido. Revisa comas faltantes o comillas.');
                            setSaving(prev => ({ ...prev, [slug]: false }));
                            return;
                        }
                    }
                }
   
                if (Array.isArray(parsedSchedule) || parsedSchedule === null) {
                    
                    promises.push(api.patch(`/admin/events/featured-events/${slug}/schedule`, {
                        schedule_data: parsedSchedule
                    }));
                } else {
                    console.error('❌ [DEBUG] Formato final inválido:', parsedSchedule);
                    alert('⚠️ El cronograma debe ser un array válido de hitos o estar vacío.');
                    setSaving(prev => ({ ...prev, [slug]: false }));
                    return;
                }
            }
   
            // Ejecutar todas las promesas en paralelo
            
            await Promise.all(promises);
   
            // Actualizar estado local
            setLocalChanges(prev => ({
                ...prev,
                [slug]: { ...prev[slug], hasChanges: false }
            }));
   
            setFeaturedEvents(prev => prev.map(event => 
                event.slug === slug ? { ...event, ...changes } : event
            ));
            
            alert('✅ Cambios guardados correctamente');
        } catch (err) {
            console.error('❌ [DEBUG] Error en saveEventChanges:', err);
            alert('Error al guardar los cambios: ' + (err.response?.data?.message || err.message));
        } finally {
            setSaving(prev => ({ ...prev, [slug]: false }));
        }
    };
    // Cancelar cambios no guardados
    const cancelChanges = (slug) => {
        const originalEvent = featuredEvents.find(e => e.slug === slug);
        if (originalEvent) {
            setLocalChanges(prev => ({
                ...prev,
                [slug]: {
                    end_date: originalEvent.end_date,
                    event_status: originalEvent.event_status || 'scheduled',
                    status_message: originalEvent.status_message || '',
                    location_name: originalEvent.location_name || '',
                    location_address: originalEvent.location_address || '',
                    location_city: originalEvent.location_city || '',
                    location_country: originalEvent.location_country || 'Bolivia',
                    location_map_url: originalEvent.location_map_url || '',
                    hasChanges: false
                }
            }));
        }
    };

    // Alternar evento destacado (add/remove)
    const handleFeaturedToggle = async (slug, isFeatured) => {
        try {
            await api.patch(`/admin/events/featured-events/${slug}`, { 
                action: isFeatured ? 'remove' : 'add' 
            });
            
            if (isFeatured) {
                setFeaturedEvents(prev => prev.filter(e => e.slug !== slug));
            } else {
                const eventToAdd = events.find(e => e.slug === slug);
                if (eventToAdd) {
                    // Recargar datos para obtener el registro completo
                    const featuredRes = await api.get('/admin/events/featured-events');
                    setFeaturedEvents(featuredRes.data);
                }
            }
        } catch (err) {
            console.error('Error:', err);
            alert('Error al actualizar evento destacado');
        }
    };

    // Actualizar orden
    const handleOrderChange = async (slug, newOrder) => {
        try {
            await api.patch(`/admin/events/featured-events/${slug}/order`, { 
                order: newOrder 
            });
            
            setFeaturedEvents(prev => {
                const updated = [...prev];
                const index = updated.findIndex(e => e.slug === slug);
                if (index >= 0) {
                    const [moved] = updated.splice(index, 1);
                    updated.splice(newOrder, 0, moved);
                    return updated.map((e, i) => ({ ...e, featured_order: i }));
                }
                return updated;
            });
        } catch (err) {
            console.error('Error:', err);
            alert('Error al actualizar el orden');
        }
    };

    // Actualizar visibilidad (show_in_home)
    const handleVisibilityToggle = async (slug, currentVisibility) => {
        try {
            const res = await api.patch(`/admin/events/${slug}/visibility`);
            
            setEvents(prev => prev.map(event => 
                event.slug === slug ? { 
                    ...event, 
                    show_in_home: res.data.show_in_home 
                } : event
            ));
    
            // Si se ocultó y estaba destacado, recargar destacados
            if (res.data.show_in_home === false) {
                const featuredRes = await api.get('/admin/events/featured-events');
                setFeaturedEvents(featuredRes.data);
            }
        } catch (err) {
            console.error('Error:', err);
            alert('Error al actualizar visibilidad');
        }
    };

    // 💰 Expandir/colapsar panel de fees
const toggleFeeSection = async (slug) => {
    const current = feeConfigs[slug];

    // ------------------------------------------
    // CERRAR
    // ------------------------------------------

    if (current?.expanded) {
        setFeeConfigs(prev => ({
            ...prev,
            [slug]: {
                ...prev[slug],
                expanded: false
            }
        }));

        return;
    }

    // ------------------------------------------
    // YA CARGADO → SOLO ABRIR
    // ------------------------------------------

    if (current?.loaded) {
        setFeeConfigs(prev => ({
            ...prev,
            [slug]: {
                ...prev[slug],
                expanded: true
            }
        }));

        return;
    }

    // ------------------------------------------
    // CARGANDO
    // ------------------------------------------

    setFeeConfigs(prev => ({
        ...prev,
        [slug]: {
            ...(prev[slug] || {}),
            expanded: true,
            loading: true
        }
    }));

    try {

        const res = await api.get(
            `/admin/fee-config/${slug}`
        );

        const data = res.data || {};

        setFeeConfigs(prev => ({
            ...prev,

            [slug]: {

                qr: {
                    is_enabled: !!data.qr?.is_enabled,
                    fee_type: data.qr?.fee_type || 'fixed',
                    fee_value: data.qr?.fee_value ?? 0
                },

                stripe: {
                    is_enabled: !!data.stripe?.is_enabled,
                    fee_type: data.stripe?.fee_type || 'fixed',
                    fee_value: data.stripe?.fee_value ?? 0
                },

                expanded: true,
                loaded: true,
                loading: false,
                saving: false
            }
        }));

    } catch (err) {

        console.error(
            'Error al cargar fee:',
            err
        );

        alert(
            'Error al cargar la configuración de fee: ' +
            (
                err.response?.data?.error ||
                err.message
            )
        );

        setFeeConfigs(prev => ({
            ...prev,
            [slug]: {
                ...(prev[slug] || {}),
                expanded: true,
                loading: false
            }
        }));
    }
};

const updateFeeLocal = (slug, method, field, value) => {

    setFeeConfigs(prev => ({
        ...prev,

        [slug]: {
            ...prev[slug],

            [method]: {
                ...prev[slug]?.[method],
                [field]: value
            }
        }
    }));
};

const saveFeeConfig = async (slug) => {

    const config = feeConfigs[slug];

    if (!config) return;


    // ==========================================
    // VALIDAR QR
    // ==========================================

    const qrValue = parseFloat(
        config.qr?.fee_value ?? 0
    );

    if (isNaN(qrValue) || qrValue < 0) {

        alert(
            'El fee de QR debe ser un número mayor o igual a 0'
        );

        return;
    }

    if (
        config.qr?.fee_type === 'percentage' &&
        qrValue > 100
    ) {

        alert(
            'El porcentaje de QR no puede ser mayor a 100'
        );

        return;
    }


    // ==========================================
    // VALIDAR STRIPE
    // ==========================================

    const stripeValue = parseFloat(
        config.stripe?.fee_value ?? 0
    );

    if (isNaN(stripeValue) || stripeValue < 0) {

        alert(
            'El fee de Stripe debe ser un número mayor o igual a 0'
        );

        return;
    }

    if (
        config.stripe?.fee_type === 'percentage' &&
        stripeValue > 100
    ) {

        alert(
            'El porcentaje de Stripe no puede ser mayor a 100'
        );

        return;
    }


    // ==========================================
    // GUARDANDO
    // ==========================================

    setFeeConfigs(prev => ({
        ...prev,

        [slug]: {
            ...prev[slug],
            saving: true
        }
    }));


    try {

        await api.put(
            `/admin/fee-config/${slug}`,
            {
                qr: {
                    is_enabled:
                        !!config.qr?.is_enabled,

                    fee_type:
                        config.qr?.fee_type || 'fixed',

                    fee_value:
                        qrValue
                },

                stripe: {
                    is_enabled:
                        !!config.stripe?.is_enabled,

                    fee_type:
                        config.stripe?.fee_type || 'fixed',

                    fee_value:
                        stripeValue
                }
            }
        );


        alert(
            '✅ Configuración de fees guardada correctamente'
        );

    } catch (err) {

        console.error(
            'Error al guardar fee:',
            err
        );

        alert(
            'Error al guardar los fees: ' +
            (
                err.response?.data?.error ||
                err.message
            )
        );

    } finally {

        setFeeConfigs(prev => ({
            ...prev,

            [slug]: {
                ...prev[slug],
                saving: false
            }
        }));
    }
};

    if (loading) return <div className="loading">Cargando...</div>;

    if (error) return <div className="error">Error: {error}</div>;

    return (
        <div className="featured-admin-container">
            <div className="featured-admin-header">
                <h1>Gestión de Eventos Destacados</h1>
                <button onClick={() => navigate(-1)} className="featured-admin-back-btn">
                    Volver a Eventos
                </button>
            </div>
            
            <div className="featured-events-section">
                <h2>Eventos Destacados Actuales</h2>
                {featuredEvents.length === 0 && (
                    <p className="no-featured-message">No hay eventos destacados. Selecciona "Destacar" en un evento de la lista inferior.</p>
                )}
                {featuredEvents.map((event, index) => {
                    const changes = localChanges[event.slug] || {};
                    const hasChanges = changes.hasChanges;
                    const isSaving = saving[event.slug];
                    
                    return (
                        <div key={event.slug} className="featured-event-card">
                            <h3>{event.title || event.slug}</h3>

                            {/* Estado del Evento */}
                            <div className="event-status-control">
                                <label>Estado del Evento:</label>
                                <select 
                                    value={changes.event_status || event.event_status || 'scheduled'}
                                    onChange={(e) => updateLocalChange(event.slug, 'event_status', e.target.value)}
                                    className="event-status-select"
                                >
                                    <option value="scheduled">📅 Fecha Confirmada</option>
                                    <option value="rescheduled">🔄 Reprogramado</option>
                                    <option value="cancelled">❌ Cancelado</option>
                                    <option value="tbd">⏰ Por Confirmar</option>
                                </select>
                            </div>
                            
                            {/* Mensaje personalizado */}
                            {((changes.event_status || event.event_status) === 'rescheduled' || 
                              (changes.event_status || event.event_status) === 'cancelled') && (
                                <div className="status-message-control">
                                    <label>Mensaje:</label>
                                    <input 
                                        type="text"
                                        value={changes.status_message !== undefined ? changes.status_message : (event.status_message || '')}
                                        onChange={(e) => updateLocalChange(event.slug, 'status_message', e.target.value)}
                                        placeholder={(changes.event_status || event.event_status) === 'rescheduled' ? 
                                            "Ej: Nueva fecha: 15 de diciembre, 2024" : 
                                            "Motivo de cancelación"}
                                        className="status-message-input"
                                    />
                                </div>
                            )}
                            
                            {/* Fecha del Evento */}
                            {(changes.event_status || event.event_status) !== 'cancelled' && (
                                <div className="date-picker-group">
                                    <label>Fecha del Evento:</label>
                                    <DatePicker
                                        selected={changes.end_date ? new Date(changes.end_date) : (event.end_date ? new Date(event.end_date) : null)}
                                        onChange={(date) => updateLocalChange(event.slug, 'end_date', date)}
                                        placeholderText="Seleccionar fecha del evento"
                                        className="featured-date-picker"
                                    />
                                </div>
                            )}

                            {/* Ubicación */}
                            <div className="featured-location-section">
                                <h4>📍 Ubicación del Evento</h4>
                                
                                <div className="location-form-group">
                                    <label>Nombre del Lugar:</label>
                                    <input
                                        type="text"
                                        value={changes.location_name !== undefined ? changes.location_name : (event.location_name || '')}
                                        onChange={(e) => updateLocalChange(event.slug, 'location_name', e.target.value)}
                                        placeholder="Ej: Estadio Olímpico"
                                        className="location-input"
                                    />
                                </div>
                                
                                <div className="location-form-group">
                                    <label>Dirección:</label>
                                    <textarea
                                        value={changes.location_address !== undefined ? changes.location_address : (event.location_address || '')}
                                        onChange={(e) => updateLocalChange(event.slug, 'location_address', e.target.value)}
                                        placeholder="Dirección completa"
                                        className="location-textarea"
                                        rows="2"
                                    />
                                </div>
                                
                                <div className="location-row">
                                    <div className="location-form-group">
                                        <label>Ciudad:</label>
                                        <input
                                            type="text"
                                            value={changes.location_city !== undefined ? changes.location_city : (event.location_city || '')}
                                            onChange={(e) => updateLocalChange(event.slug, 'location_city', e.target.value)}
                                            placeholder="Ciudad"
                                            className="location-input"
                                        />
                                    </div>
                                    
                                    <div className="location-form-group">
                                        <label>País:</label>
                                        <input
                                            type="text"
                                            value={changes.location_country !== undefined ? changes.location_country : (event.location_country || 'Bolivia')}
                                            onChange={(e) => updateLocalChange(event.slug, 'location_country', e.target.value)}
                                            className="location-input"
                                        />
                                    </div>
                                </div>
                                
                                <div className="location-form-group">
                                    <label>Google Maps URL:</label>
                                    <input
                                        type="url"
                                        value={changes.location_map_url !== undefined ? changes.location_map_url : (event.location_map_url || '')}
                                        onChange={(e) => updateLocalChange(event.slug, 'location_map_url', e.target.value)}
                                        placeholder="https://maps.google.com/?q=..."
                                        className="location-input"
                                    />
                                </div>
                            </div>

                            {/* 🆕 SECCIÓN: CRONOGRAMA DEL EVENTO (JSON) */}
<div className="featured-schedule-section" style={{ marginTop: '20px', padding: '15px', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #dee2e6' }}>
    <h4 style={{ marginBottom: '10px', color: '#495057', fontSize: '14px' }}>📅 Cronograma del Evento (JSON)</h4>
    <p style={{ fontSize: '12px', color: '#6c757d', marginBottom: '10px' }}>
        Define los hitos del evento. Usa el botón para copiar una plantilla inteligente basada en la fecha del evento.
    </p>
    
    <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
        <button 
            type="button"
            onClick={() => {
                // 🧠 LÓGICA INTELIGENTE: Calcular fechas reales basadas en end_date
                const baseDate = changes.end_date || event.end_date;
                let kitDateStr = "Sábado, 15 de oct";
                let raceDateStr = "Domingo, 16 de oct";

                if (baseDate) {
                    try {
                        const raceDate = new Date(baseDate);
                        const kitDate = new Date(raceDate);
                        kitDate.setDate(raceDate.getDate() - 2); // 2 días antes

                        const options = { weekday: 'long', day: 'numeric', month: 'short' };
                        raceDateStr = raceDate.toLocaleDateString('es-ES', options);
                        kitDateStr = kitDate.toLocaleDateString('es-ES', options);
                        
                        // Capitalizar primera letra
                        raceDateStr = raceDateStr.charAt(0).toUpperCase() + raceDateStr.slice(1);
                        kitDateStr = kitDateStr.charAt(0).toUpperCase() + kitDateStr.slice(1);
                    } catch (e) {
                        // Fallback a estático si falla el parseo
                    }
                }

                const template = [
                    { 
                        "id": 1, 
                        "badge": "Día 1", 
                        "title": "Entrega de Kits", 
                        "icon": "FaShoppingBag", 
                        "date": kitDateStr, 
                        "time": "10:00 - 18:00" 
                    },
                    { 
                        "id": 2, 
                        "badge": "Día del Evento", 
                        "title": "Concentración y Largada", 
                        "icon": "FaFlag", 
                        "date": raceDateStr, 
                        "time": "06:30 AM" 
                    },
                    { 
                        "id": 3, 
                        "badge": "Día del Evento", 
                        "title": "Cierre de Meta y Premiación", 
                        "icon": "FaTrophy", 
                        "date": raceDateStr, 
                        "time": "11:00 AM" 
                    }
                ];
                
                navigator.clipboard.writeText(JSON.stringify(template, null, 2));
                alert('✅ Plantilla copiada al portapapeles. Pégala en el campo de texto y ajusta las horas.');
            }}
            style={{ padding: '6px 12px', background: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '5px' }}
        >
            📋 Copiar Plantilla Inteligente
        </button>
    </div>

    <textarea
        // Manejo seguro del valor: si es objeto, lo stringify; si es string, lo usa; si es null, vacío.
        value={(() => {
            const val = changes.schedule_data !== undefined ? changes.schedule_data : (event.schedule_data || null);
            return val ? (typeof val === 'string' ? val : JSON.stringify(val, null, 2)) : '';
        })()}
        onChange={(e) => {
            const rawValue = e.target.value;
            try {
                // Intentamos parsear para guardarlo como objeto limpio en el estado
                const parsed = rawValue.trim() === '' ? null : JSON.parse(rawValue);
                updateLocalChange(event.slug, 'schedule_data', parsed);
            } catch (err) {
                // Si el usuario está a medio escribir y el JSON es inválido, 
                // guardamos el string temporalmente para que no pierda lo que escribe.
                // Al guardar, el saveEventChanges validará que sea JSON válido.
                updateLocalChange(event.slug, 'schedule_data', rawValue);
            }
        }}
        rows="8"
        style={{
            width: '100%',
            fontFamily: 'monospace',
            fontSize: '12px',
            backgroundColor: '#fff',
            color: '#212529',
            border: '1px solid #ced4da',
            borderRadius: '4px',
            padding: '10px',
            boxSizing: 'border-box'
        }}
        placeholder='[
            {
              "id": 1,
              "badge": "Viernes 11 y Sábado 12 de Septiembre",
              "title": "Entrega de Dorsales",
              "icon": "FaShoppingBag",
              "date": "11 y 12 de septiembre 2026",
              "time": "Por confirmar"
            },
            {
              "id": 2,
              "badge": "Día del Evento",
              "title": "Concentración",
              "icon": "FaUsers",
              "date": "Domingo 13 de septiembre 2026",
              "time": "07:30"
            },
            {
              "id": 3,
              "badge": "Día del Evento",
              "title": "Largada Oficial",
              "icon": "FaFlag",
              "date": "Domingo 13 de septiembre 2026",
              "time": "08:00"
            },
            {
              "id": 4,
              "badge": "Día del Evento",
              "title": "Cierre de Meta",
              "icon": "FaFlagCheckered",
              "date": "Domingo 13 de septiembre 2026",
              "time": "10:00"
            },
            {
              "id": 5,
              "badge": "Día del Evento",
              "title": "Premiación",
              "icon": "FaTrophy",
              "date": "Domingo 13 de septiembre 2026",
              "time": "Inmediatamente después del cierre"
            }
          ]'
    />
    <small style={{ color: '#888', fontSize: '11px', display: 'block', marginTop: '5px' }}>
        💡 <b>Iconos disponibles:</b> FaShoppingBag, FaUsers, FaFlag, FaFlagCheckered, FaTrophy, FaClock, FaMapMarkerAlt
    </small>
</div>
                            
                            {/* Orden y botones de acción */}
                            <div className="featured-order-control">
                                <label>Orden:</label>
                                <select
                                    value={index}
                                    onChange={(e) => handleOrderChange(event.slug, parseInt(e.target.value))}
                                    className="featured-order-select"
                                >
                                    {featuredEvents.map((_, i) => (
                                        <option key={i} value={i}>{i + 1}</option>
                                    ))}
                                </select>
                            </div>
                            
                            <div className="action-buttons">
                                <button 
                                    onClick={() => handleFeaturedToggle(event.slug, true)}
                                    className="featured-remove-btn"
                                >
                                    Quitar de Destacados
                                </button>
                                
                                {hasChanges && (
                                    <>
                                        <button 
                                            onClick={() => saveEventChanges(event.slug)}
                                            className="save-changes-btn"
                                            disabled={isSaving}
                                        >
                                            {isSaving ? 'Guardando...' : '💾 Guardar Cambios'}
                                        </button>
                                        <button 
                                            onClick={() => cancelChanges(event.slug)}
                                            className="cancel-changes-btn"
                                        >
                                            Cancelar
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
            
            {/* Sección de Todos los Eventos (sin cambios) */}
            <div className="all-events-section">
                <h2>Todos los Eventos</h2>
                {events.map(event => {
                    const fee = feeConfigs[event.slug] || {};
                    return (
                    <div key={event.slug} className="regular-event-card">
                        <h3>{event.title || event.slug}</h3>
                        <div className="event-controls-group">
                            {!featuredEvents.some(f => f.slug === event.slug) && (
                                <button
                                    onClick={() => handleFeaturedToggle(event.slug, false)}
                                    className="featured-add-btn"
                                >
                                    ★ Destacar
                                </button>
                            )}
                            <button
                                onClick={() => handleVisibilityToggle(event.slug, event.show_in_home)}
                                className={`visibility-toggle-btn ${event.show_in_home ? 'is-visible' : 'is-hidden'}`}
                            >
                                {event.show_in_home ? '👁️ Ocultar' : '👁️‍🗨️ Mostrar'}
                            </button>
                            <button
                                onClick={() => toggleFeeSection(event.slug)}
                                className="fee-toggle-btn"
                            >
                                💰 Fee de Plataforma {fee.expanded ? '▲' : '▼'}
                            </button>
                        </div>

                        {/* 💰 Panel de configuración de fees */}
{fee.expanded && (
    <div className="fee-config-section">

        {fee.loading ? (

            <p>Cargando configuración...</p>

        ) : (

            <>

                {/* ==========================================
                    QR
                ========================================== */}

                <div className="fee-method-card">

                    <h4>
                        🏦 Pago mediante QR
                    </h4>

                    <div className="fee-form-group">

                        <label>

                            <input
                                type="checkbox"
                                checked={
                                    !!fee.qr?.is_enabled
                                }
                                onChange={(e) =>
                                    updateFeeLocal(
                                        event.slug,
                                        'qr',
                                        'is_enabled',
                                        e.target.checked
                                    )
                                }
                            />

                            {' '}
                            Activar fee para pagos QR

                        </label>

                    </div>


                    <div className="fee-form-row">

                        <div className="fee-form-group">

                            <label>
                                Tipo de Fee / Tasa:
                            </label>

                            <select
                                value={
                                    fee.qr?.fee_type ||
                                    'fixed'
                                }
                                onChange={(e) =>
                                    updateFeeLocal(
                                        event.slug,
                                        'qr',
                                        'fee_type',
                                        e.target.value
                                    )
                                }
                                disabled={
                                    !fee.qr?.is_enabled
                                }
                                className="fee-type-select"
                            >

                                <option value="fixed">
                                    Monto Fijo (Bs.)
                                </option>

                                <option value="percentage">
                                    Porcentaje (%)
                                </option>

                            </select>

                        </div>


                        <div className="fee-form-group">

                            <label>

                                {fee.qr?.fee_type ===
                                'percentage'

                                    ? 'Porcentaje (%):'

                                    : 'Monto (Bs.):'

                                }

                            </label>

                            <input
                                type="number"
                                min="0"
                                max={
                                    fee.qr?.fee_type ===
                                    'percentage'
                                        ? 100
                                        : undefined
                                }
                                step="0.01"
                                value={
                                    fee.qr?.fee_value ??
                                    0
                                }
                                onChange={(e) =>
                                    updateFeeLocal(
                                        event.slug,
                                        'qr',
                                        'fee_value',
                                        e.target.value
                                    )
                                }
                                disabled={
                                    !fee.qr?.is_enabled
                                }
                                className="fee-value-input"
                            />

                        </div>

                    </div>

                </div>


                {/* ==========================================
                    STRIPE
                ========================================== */}

                <div className="fee-method-card">

                    <h4>
                        💳 Pago mediante Tarjeta / Stripe
                    </h4>

                    <div className="fee-form-group">

                        <label>

                            <input
                                type="checkbox"
                                checked={
                                    !!fee.stripe?.is_enabled
                                }
                                onChange={(e) =>
                                    updateFeeLocal(
                                        event.slug,
                                        'stripe',
                                        'is_enabled',
                                        e.target.checked
                                    )
                                }
                            />

                            {' '}
                            Activar fee para pagos con tarjeta

                        </label>

                    </div>


                    <div className="fee-form-row">

                        <div className="fee-form-group">

                            <label>
                                Tipo de Fee / Tasa:
                            </label>

                            <select
                                value={
                                    fee.stripe?.fee_type ||
                                    'fixed'
                                }
                                onChange={(e) =>
                                    updateFeeLocal(
                                        event.slug,
                                        'stripe',
                                        'fee_type',
                                        e.target.value
                                    )
                                }
                                disabled={
                                    !fee.stripe?.is_enabled
                                }
                                className="fee-type-select"
                            >

                                <option value="fixed">
                                    Monto Fijo ($us)
                                </option>

                                <option value="percentage">
                                    Porcentaje (%)
                                </option>

                            </select>

                        </div>


                        <div className="fee-form-group">

                            <label>

                                {fee.stripe?.fee_type ===
                                'percentage'

                                    ? 'Porcentaje (%):'

                                    : 'Monto ($us):'

                                }

                            </label>

                            <input
                                type="number"
                                min="0"
                                max={
                                    fee.stripe?.fee_type ===
                                    'percentage'
                                        ? 100
                                        : undefined
                                }
                                step="0.01"
                                value={
                                    fee.stripe?.fee_value ??
                                    0
                                }
                                onChange={(e) =>
                                    updateFeeLocal(
                                        event.slug,
                                        'stripe',
                                        'fee_value',
                                        e.target.value
                                    )
                                }
                                disabled={
                                    !fee.stripe?.is_enabled
                                }
                                className="fee-value-input"
                            />

                        </div>

                    </div>

                </div>


                {/* ==========================================
                    GUARDAR
                ========================================== */}

                <button
                    onClick={() =>
                        saveFeeConfig(event.slug)
                    }
                    className="fee-save-btn"
                    disabled={fee.saving}
                >

                    {fee.saving
                        ? 'Guardando...'
                        : '💾 Guardar Configuración de Fees'
                    }

                </button>

            </>

        )}

    </div>
)}
                    </div>
                    );
                })}
            </div>
        </div>
    );
};

export default FeaturedEventsAdmin;